import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, HoverCard } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';
import type { EditorRoleRow } from './types';
import { RolePickerPopover } from './RolePickerPopover';
import styles from './RoleEditorRowCells.module.css';

/**
 * EAA-1884: `RoleEditorTable`'s "Vai trò chuẩn" column cell. Split out of the
 * old hand-rolled `<tr>`/`<td>` `RoleEditorRow` (now two `DataTable`
 * `ColumnConfig.render` cells) so the table itself can be the shared kit
 * `DataTable` instead of a raw `<table>`.
 *
 * EAA-1817 (design change 2026-08-24): TWO LINES, name first. This cell used
 * to lead with the `role_key` and put "{roleName} · N Level · M liên kết"
 * underneath as a meta note. The handoff reversed that — "tên tiếng Việt là
 * dòng chính (13–13.5px, #1f2933), mã vai trò xuống dưới dạng mono 11px
 * #94a1ad" — because an admin scanning for "Thủ thư" cannot find it in a
 * column of `librarian`/`counselor`/`it_admin`.
 *
 * THE LEVEL/LINK COUNTS ARE NO LONGER PRINTED, and that is a real loss rather
 * than a tidy-up: frame 55 draws exactly two lines in this cell and has no
 * third slot, and the link count is already visible as the chips in the next
 * column. They are kept in the row's screen-reader text so the information is
 * not deleted outright, but a sighted reader can no longer see the Level count
 * here. Recorded in the SRS; if it needs to come back it needs a column of its
 * own, not a third line in this one.
 */
export function RoleEditorIdentityCell({ row }: { row: EditorRoleRow }) {
    const { t } = useTranslation();
    return (
        <span className={styles.roleIdentity}>
            <span className={styles.roleIdentityName}>{row.roleName || row.roleKey}</span>
            <code className={styles.roleIdentityKey}>{row.roleKey}</code>
            <span className="portal-sr-only">
                {t('console.roleCoverage.editorRow.roleMetaSr', {
                    levelCount: row.levelCount,
                    linkCount: row.linkCount,
                })}
            </span>
        </span>
    );
}

export interface RoleEditorChipsCellProps {
    row: EditorRoleRow;
    appName: string;
    canManage: boolean;
    appRoleOptions: AppRoleOption[];
    appRolesLoading: boolean;
    isPickerOpen: boolean;
    onOpenPicker: () => void;
    onClosePicker: () => void;
    onAddRole: (appRole: string) => void;
    /**
     * EAA-1817 (frame 55g): this REQUESTS a removal — the parent opens a confirm
     * dialog stating how many accounts lose access. It used to perform the write
     * directly, so one click revoked access for real people with only a 10s undo
     * behind it. The name says "request" because the chip no longer knows whether
     * the removal will happen.
     */
    onRequestRemoveChip: (chip: { id: string; appRole: string; appRoleName: string }) => void;
    removePending: boolean;
}

/**
 * EAA-1884: `RoleEditorTable`'s "Vai trò trong {app}" column cell — the
 * row's N removable chips plus the "+ vai trò" trigger/popover. Kept as its
 * own component (not inlined in a `ColumnConfig.render` callback) because it
 * owns hooks: the `addButtonRef`/`wasPickerOpen` focus-restore effect below
 * needs a real component instance per row — `render` is a plain function, so
 * calling hooks directly inside it would break the rules of hooks, but
 * returning `<RoleEditorChipsCell />` from it mounts a normal component that
 * `DataTable` renders once per row exactly like before.
 */
export function RoleEditorChipsCell({
    row,
    appName,
    canManage,
    appRoleOptions,
    appRolesLoading,
    isPickerOpen,
    onOpenPicker,
    onClosePicker,
    onAddRole,
    onRequestRemoveChip,
    removePending,
}: RoleEditorChipsCellProps) {
    const { t } = useTranslation();
    const alreadyLinked = row.chips.map((chip) => chip.appRole);

    // EAA-1817: the popover unmounts on close (Escape / outside click / a
    // successful pick) with no focus management of its own — restore focus
    // to the trigger that opened it so a keyboard user isn't dropped back to
    // the top of the document. The kit `Chip` renders a plain `<span>` and
    // does not forward a ref, so the ref sits on a wrapping span instead and
    // the effect below focuses the chip's own `role="button"` element.
    const addButtonRef = useRef<HTMLSpanElement>(null);
    const wasPickerOpen = useRef(isPickerOpen);
    useEffect(() => {
        if (wasPickerOpen.current && !isPickerOpen) {
            addButtonRef.current?.querySelector<HTMLElement>('[role="button"]')?.focus();
        }
        wasPickerOpen.current = isPickerOpen;
    }, [isPickerOpen]);

    return (
        <div className="portal-console-chips">
            {/* EAA-1817: was a hand-rolled pill-shaped chip (`.portal-console-chip`)
                that visually disagreed with `RoleMapTable`'s kit `Chip`
                (rounded-rect, inset ring) for the exact same
                role↔app_role concept, one screen-switch away — now identical. */}
            {row.chips.map((chip) => {
                const removeLabel = t('console.roleCoverage.editorRow.removeChipAriaLabel', {
                    appRole: chip.appRoleName,
                    roleKey: row.roleName || row.roleKey,
                });
                // EAA-1817: `Chip` has no `disabled` affordance for `onRemove`
                // (unlike the old `.rc-chip-remove` button) — guard inside the
                // handler so a pending removal still can't be double-fired.
                const chipEl = (
                    <Chip
                        tone={chip.isStale ? 'stale' : 'mapped'}
                        // EAA-1817: name on the chip, key in the tooltip. A stale
                        // chip already carries a `HoverCard` explaining WHY it is
                        // struck through, so it gets no `title` — two tooltips on
                        // one element is one too many, and the explanation is the
                        // more useful of the two.
                        mono={false}
                        title={
                            chip.isStale
                                ? undefined
                                : t('console.roleMap.table.appRoleCodeTooltip', {
                                      code: chip.appRole,
                                  })
                        }
                        removeLabel={removeLabel}
                        onRemove={
                            canManage
                                ? () => {
                                      if (removePending) return;
                                      onRequestRemoveChip({
                                          id: chip.id,
                                          appRole: chip.appRole,
                                          appRoleName: chip.appRoleName,
                                      });
                                  }
                                : undefined
                        }
                    >
                        {chip.appRoleName}
                    </Chip>
                );

                if (!chip.isStale) {
                    return <span key={chip.id}>{chipEl}</span>;
                }

                // EAA-1817: swap the native `title` tooltip for the kit
                // `HoverCard` — same explanatory text, themed overlay.
                return (
                    <HoverCard
                        key={chip.id}
                        trigger={chipEl}
                        content={t('console.roleCoverage.editorRow.staleTooltip')}
                    />
                );
            })}

            {canManage && (
                <span className={styles.rolePickerAnchor}>
                    <span ref={addButtonRef}>
                        <Chip tone="add" onClick={onOpenPicker}>
                            {t('console.roleCoverage.editorRow.addRoleButton')}
                        </Chip>
                    </span>
                    {isPickerOpen && (
                        <RolePickerPopover
                            appName={appName}
                            roleKey={row.roleKey}
                            options={appRoleOptions}
                            loading={appRolesLoading}
                            alreadyLinked={alreadyLinked}
                            onPick={onAddRole}
                            onClose={onClosePicker}
                        />
                    )}
                </span>
            )}
        </div>
    );
}
